import React, { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { useUser } from '@auth0/nextjs-auth0';
import { useRouter } from 'next/router';
import { Button, Typography } from '@material-tailwind/react';
import { Calendar, Loader2, Users } from 'lucide-react';
import { Layout } from '../../components/shared/layout/layout';
import Toast from '../../components/shared/toast';
import UltraSeatsModal from '../../components/buy/UltraSeatsModal';
import { SubscriberExistsResponse } from '../../utils/types';
import { Edition } from '../../utils/enums';
import {
  buildUltraSignupLoginUrl,
  getUltraSignupPath,
  readUltraCheckoutIntent,
  startUltraCheckout,
  UltraBillingInterval,
  UltraCheckoutIntent,
  ULTRA_PLAN,
} from '../../utils/helpers/ultraCheckout';

const ULTRA_PLANS: {
  interval: UltraBillingInterval;
  label: string;
  listPrice: number;
  cadence: string;
  description: string;
  icon: typeof Calendar;
}[] = [
  {
    interval: 'monthly',
    label: 'Monthly',
    listPrice: 15,
    cadence: 'per user / month',
    description: 'Flexible billing. Choose seats before checkout.',
    icon: Calendar,
  },
  {
    interval: 'yearly',
    label: 'Yearly',
    listPrice: 150,
    cadence: 'per user / year',
    description: 'Best value. Choose seats before checkout.',
    icon: Users,
  },
];

function formatUsd(amount: number): string {
  return amount % 1 === 0 ? `$${amount}` : `$${amount.toFixed(2)}`;
}

export default function BuyUltraPage() {
  const { user, isLoading: isUserLoading } = useUser();
  const router = useRouter();

  const [intent, setIntent] = useState<UltraCheckoutIntent>({ plan: ULTRA_PLAN });
  const [readyForCheckout, setReadyForCheckout] = useState(false);
  const [isCheckingAccount, setIsCheckingAccount] = useState(false);
  const [isTeamBlocked, setIsTeamBlocked] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [selectedInterval, setSelectedInterval] =
    useState<UltraBillingInterval | null>(null);
  const openedSeatsFromLink = useRef(false);

  const coupon = intent.coupon ?? '';

  const beginCheckout = async (interval: UltraBillingInterval, seats = 1) => {
    if (!user || isStartingCheckout) return;
    setIsStartingCheckout(true);
    try {
      await startUltraCheckout({
        interval,
        seats,
        ...(coupon ? { coupon } : {}),
      });
    } catch (err) {
      setIsStartingCheckout(false);
      setSelectedInterval(null);
      setToastMessage(
        err instanceof Error
          ? err.message
          : 'Unable to start Ultra checkout. Please try again.'
      );
      setShowToast(true);
    }
  };

  useEffect(() => {
    if (!router.isReady || isUserLoading) return;

    const next = readUltraCheckoutIntent(router.query) ?? { plan: ULTRA_PLAN };
    setIntent(next);

    if (user || isRedirecting) return;
    setIsRedirecting(true);
    window.location.href = buildUltraSignupLoginUrl(next);
  }, [router.isReady, router.query, isUserLoading, user, isRedirecting]);

  useEffect(() => {
    if (!router.isReady || isUserLoading || !user) {
      setReadyForCheckout(false);
      setIsTeamBlocked(false);
      return;
    }

    let cancelled = false;
    setIsCheckingAccount(true);

    async function checkAccount() {
      try {
        const existsRes = await fetch('/api/account/subscriberExists');
        if (cancelled) return;

        const existsData: SubscriberExistsResponse | null = existsRes.ok
          ? await existsRes.json()
          : null;
        if (!existsData?.subscriberExists) {
          window.location.assign(
            getUltraSignupPath(coupon || undefined, intent.interval)
          );
          return;
        }

        if (user.sub) {
          const detailsRes = await fetch(
            `/api/user/${encodeURIComponent(user.sub)}/accountDetails`
          );
          if (cancelled) return;
          if (detailsRes.ok) {
            const details = await detailsRes.json();
            if (details?.edition === Edition.Team) {
              setIsTeamBlocked(true);
              setReadyForCheckout(false);
              return;
            }
          }
        }

        if (cancelled) return;
        setIsTeamBlocked(false);
        setReadyForCheckout(true);
        if (intent.interval && !openedSeatsFromLink.current) {
          openedSeatsFromLink.current = true;
          setSelectedInterval(intent.interval);
        }
      } catch {
        if (!cancelled) setReadyForCheckout(false);
      } finally {
        if (!cancelled) setIsCheckingAccount(false);
      }
    }

    checkAccount();
    return () => {
      cancelled = true;
    };
  }, [router.isReady, isUserLoading, user, coupon, intent.interval]);

  const loadingMessage = isStartingCheckout
    ? coupon
      ? 'Applying campaign discount…'
      : 'Opening checkout…'
    : !user
    ? 'Redirecting to sign in…'
    : 'Loading Ultra checkout…';

  const loader = (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center bg-[#FDFDFD]">
      <div className="flex flex-col items-center gap-3 text-gray-600">
        <Loader2 className="animate-spin text-[#2CA6A4]" />
        <p className="text-sm font-medium">{loadingMessage}</p>
      </div>
    </div>
  );

  if (
    !router.isReady ||
    isUserLoading ||
    !user ||
    isRedirecting ||
    isStartingCheckout ||
    (user && isCheckingAccount)
  ) {
    return (
      <>
        <Head>
          <title>Ultra | Confirmed</title>
        </Head>
        <Layout>{loader}</Layout>
      </>
    );
  }

  if (isTeamBlocked) {
    return (
      <>
        <Head>
          <title>Ultra | Confirmed</title>
        </Head>
        <Layout>
          <div className="min-h-[calc(100vh-80px)] bg-[#FDFDFD] flex items-center justify-center px-4">
            <div className="max-w-md w-full bg-white rounded-3xl shadow-sm border border-gray-100 p-8 text-center">
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-teal-600" />
              </div>
              <Typography variant="h4" className="text-gray-900 font-bold mb-2">
                You already have Team
              </Typography>
              <Typography className="text-gray-600 text-sm mb-6">
                Ultra checkout is for upgrading to Team. Your account already
                has the Team edition, so this page is not available.
              </Typography>
              <div className="flex flex-col gap-3">
                <Button
                  className="w-full bg-confirmed-secondary hover:bg-secondary-hover text-white py-3 rounded-xl font-bold text-sm"
                  onClick={() => router.push('/user/account')}
                >
                  Go to Account
                </Button>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-700 font-bold text-xs py-1"
                  onClick={() => router.push('/meetings')}
                >
                  Back to Meetings
                </button>
              </div>
            </div>
          </div>
        </Layout>
      </>
    );
  }

  if (readyForCheckout && user) {
    const hasCampaign = Boolean(coupon);
    const selectedPlan = ULTRA_PLANS.find(
      (plan) => plan.interval === selectedInterval
    );
    const selectedUnitPrice = selectedPlan
      ? selectedPlan.listPrice
      : 0;

    return (
      <>
        <Head>
          <title>Choose Ultra | Confirmed</title>
        </Head>
        <Layout>
          <div className="min-h-[calc(100vh-80px)] bg-[#FDFDFD]">
            <div className="relative overflow-hidden bg-white border-b border-gray-100">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-[#2CA6A4]/5 to-transparent rounded-full blur-3xl -z-10 opacity-60" />
              <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
                <Typography
                  variant="small"
                  className="text-confirmed-secondary font-bold uppercase tracking-wider mb-3"
                >
                  Confirmed Superconnector Ultra
                </Typography>
                <Typography
                  variant="h1"
                  className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight mb-4"
                >
                  Choose your <span className="text-[#2CA6A4]">Ultra</span> plan
                </Typography>
                <Typography
                  variant="lead"
                  className="max-w-2xl mx-auto text-lg text-gray-600 font-medium leading-relaxed"
                >
                  {hasCampaign
                    ? 'Campaign pricing is applied at checkout. Choose how many seats you need.'
                    : '$15 per user / month, or $150 per user / year. Choose how many seats you need.'}
                </Typography>
              </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
              <div className="grid gap-6 md:grid-cols-2">
                {ULTRA_PLANS.map((plan) => {
                  const Icon = plan.icon;
                  const displayPrice = plan.listPrice;

                  return (
                    <div
                      key={plan.interval}
                      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 flex flex-col hover:shadow-md transition-all duration-300"
                    >
                      <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-teal-600" />
                      </div>
                      <p className="text-sm font-bold uppercase tracking-wide text-[#2CA6A4]">
                        {plan.label}
                      </p>
                      <div className="mt-3 flex items-end gap-2">
                        {hasCampaign && (
                          <span className="text-lg text-gray-400 line-through mb-1">
                            {formatUsd(plan.listPrice)}
                          </span>
                        )}
                        <span className="text-4xl font-black text-gray-900 tracking-tight">
                          {formatUsd(displayPrice)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {plan.cadence}
                      </p>
                      <p className="text-gray-600 text-sm mt-4 mb-8 flex-grow">
                        {plan.description}
                      </p>
                      <Button
                        size="lg"
                        className="w-full bg-confirmed-secondary hover:bg-secondary-hover text-white py-3.5 rounded-xl shadow-md hover:shadow-confirmed-secondary/20 transition-all font-bold text-sm"
                        onClick={() => {
                          if (!isStartingCheckout) {
                            setSelectedInterval(plan.interval);
                          }
                        }}
                        disabled={isStartingCheckout}
                      >
                        Continue with {plan.label.toLowerCase()}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            <UltraSeatsModal
              open={Boolean(selectedInterval)}
              interval={selectedInterval}
              planLabel={selectedPlan?.label ?? ''}
              unitPrice={selectedUnitPrice}
              cadence={selectedPlan?.cadence ?? ''}
              isSubmitting={isStartingCheckout}
              onClose={() => {
                if (!isStartingCheckout) setSelectedInterval(null);
              }}
              onConfirm={(seats) =>
                selectedInterval && beginCheckout(selectedInterval, seats)
              }
            />

            <div className="fixed top-5 right-5 z-50 w-96">
              <Toast
                show={showToast}
                setShow={setShowToast}
                alertMessage={toastMessage}
                color="red"
              />
            </div>
          </div>
        </Layout>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>Ultra | Confirmed</title>
      </Head>
      <Layout>{loader}</Layout>
    </>
  );
}
